import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { diagnosticVariant } from '@/utils/diagnosticVariant';

/**
 * The single, non-salesy CTA for the column: a warm invitation to the free
 * diagnostic. Styled as a sibling of the Pricing "Intelligent Value" card
 * (primary-50 surface, border-2, hard ledge) so the blog reads as its relative.
 * UTM is baked in so blog-sourced diagnostics are attributable in GA4.
 */

interface DiagnosticCTAProps {
  /** Distinguishes a post-end placement from the index placement in analytics. */
  campaign?: string;
}

const DiagnosticCTA: React.FC<DiagnosticCTAProps> = ({ campaign = 'blog_cta' }) => {
  const href = `/try?utm_source=blog&utm_medium=blog&utm_campaign=${encodeURIComponent(campaign)}`;

  // The six turns give no score, no weak-area read and no Pass Plan, so that arm never promises them.
  const sixTurns = diagnosticVariant() !== 'classic';

  return (
    <div className="bg-primary-50 rounded-2xl p-6 sm:p-8 border-2 border-primary-200 shadow-ledge">
      <p className="eyebrow mb-2">Before your next study session</p>
      <h2 className="font-display font-bold text-2xl text-neutral-900 mb-3">
        {sixTurns ? 'Try six real ASWB moments, free' : 'Find your weakest area, free 3-minute diagnostic'}
      </h2>
      <p className="text-base text-neutral-700 mb-6 max-w-xl">
        {sixTurns
          ? 'A question that adapts to you, a clinical simulation, a timed question, a flashcard, and a question for Pam.'
          : 'No credit card, no pressure. Just an honest read on the one content area most likely to cost you the exam this time around.'}
      </p>
      <Link
        to={href}
        className="btn-primary"
      >
        {sixTurns ? 'Try the six turns' : 'Take the free diagnostic'}
        <ArrowRight className="w-4 h-4" />
      </Link>
      <p className="text-sm text-neutral-600 mt-4">
        {sixTurns
          ? 'Free, no account needed. Questions reviewed by Pam Coin, LCSW.'
          : 'Three minutes. See your result and get the 15-page 2026 Pass Plan workbook, just your email. No account, no card.'}
      </p>
    </div>
  );
};

export default DiagnosticCTA;
